import { useCallback, useState } from 'react';
import { Api } from 'src/api';
import { useFormikContext } from 'formik';
import { FormMediaValue } from './S3UploadInput.types';
import { isMediaArray, isMediaCollection } from './S3UploadInput.utils';
import { calculateChunks } from 'src/services/s3-uploader';
import { AuthUtils } from 'src/services/Auth/Auth.utils';

// TODO: Make useFormikContext type generic to support forms for which media could not only be of collection
//  but under entirely different namespace
const useGenerateMultipartUpload = () => {
  const [isGenerating, setGenerating] = useState<boolean>(false);
  const { setValues } = useFormikContext<{ media: FormMediaValue<any> }>();

  const generateUpload = useCallback(
    async (model: string, files: FileList | File[], collection_name?: string) => {
      setGenerating(true);

      // Proactively refresh token before starting uploads to prevent expiration during long operations
      try {
        const jwt = AuthUtils.getJwt();
        const expiresIn = jwt.expires - Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        
        // If token expires in less than 5 minutes, refresh it now
        if (expiresIn < fiveMinutes) {
          console.log('[Upload] Token expires soon, refreshing before upload...');
          await Api.auth.refresh();
        }
      } catch (error) {
        console.warn('[Upload] Token refresh check failed, proceeding with upload:', error);
        // Continue with upload even if refresh check fails
      }

      const generatedMediaUploads = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const data = {
          mime_type: file.type,
          name: file.name,
          size: file.size,
          chunks: calculateChunks(file.size),
          collection_name: collection_name,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        try {
          const { upload_id, presigned_urls, media } = await Api.media.generateMultipartUpload(
            model,
            data,
          );

          generatedMediaUploads.push(media);

          const formMediaObj = { ...media, upload_id, presigned_urls, file };

          setValues((prev) => {
            if (isMediaArray(prev.media)) {
              return {
                ...prev,
                media: [...prev.media, formMediaObj],
              };
            }

            if (isMediaCollection(prev.media)) {
              return {
                ...prev,
                media: {
                  ...prev.media,
                  [collection_name!]: [...prev.media[collection_name!], formMediaObj],
                },
              };
            }

            return prev;
          });
        } catch (e: any) {
          setGenerating(false);
          throw e;
        }
      }

      setGenerating(false);

      return generatedMediaUploads;
    },
    [],
  );

  return {
    generateUpload,
    isGenerating,
  };
};

export { useGenerateMultipartUpload };
